import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@viastud/ui/carousel'
import { trpc } from '@viastud/ui/lib/trpc'
import { ArrowLeft } from 'lucide-react'

import { useBookingPresenter } from '@/presenters/booking.presenter'

import { BookingCard } from '../booking/booking-card.tsx'
import { BookingFilters } from '../booking/booking-filters.tsx'
import { BookingListCard } from '../booking/booking-list-card.tsx'

export function BookCourse({ setCurrentPage }: { setCurrentPage: (page: string) => void }) {
  const {
    searchValue,
    setSearchValue,
    selectedGrade,
    selectedSubject,
    setSelectedSubject,
    viewMode,
    setViewMode,
    chapters,
    selectedForYou,
    popularChapters,
    selectedModuleIds,
    handleModuleChange,
    handleBook,
    isLoading,
  } = useBookingPresenter()

  const { data: tokenInfo } = trpc.user.getTokenBalance.useQuery()
  const { data: subscription } = trpc.user.getStudentSubscriptionDetails.useQuery()

  const isSuspended = subscription?.status === 'SUSPENDED'
  const isBookingDisabled = tokenInfo?.hasSignUpBonus ?? isSuspended

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 p-6 lg:p-10">
      {/* Header */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            setCurrentPage('LESSONS')
          }}
          className="flex h-10 w-10 items-center justify-center"
        >
          <ArrowLeft className="size-6" />
        </button>
        <h1 className="text-2xl font-bold text-neutral-900">Réserver un cours</h1>
      </div>

      {isSuspended && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">
            Votre abonnement est suspendu. Veuillez régulariser votre situation pour réserver un
            cours.
          </p>
        </div>
      )}

      {/* Filters */}
      <BookingFilters
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        selectedGrade={selectedGrade}
        selectedSubject={selectedSubject}
        onSubjectChange={(v) => {
          setSelectedSubject(v as 'MATHS' | 'ALL')
        }}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <p className="text-neutral-500">Chargement des cours...</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="flex flex-col gap-12">
          {/* Sélectionné pour toi */}
          {selectedForYou.length > 0 && (
            <section className="relative">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-bold text-neutral-900">Sélectionnés pour toi</h2>
              </div>
              <Carousel
                opts={{
                  align: 'start',
                }}
                className="w-full"
              >
                <div className="absolute -top-14 right-0 flex gap-2">
                  <CarouselPrevious className="static translate-y-0" />
                  <CarouselNext className="static translate-y-0" />
                </div>
                <CarouselContent noOverFlow={true}>
                  {selectedForYou.map((chapter) => (
                    <CarouselItem
                      key={`foryou-${chapter.id}`}
                      className="md:basis-1/2 lg:basis-1/3"
                    >
                      <BookingCard
                        chapter={chapter}
                        disabled={isBookingDisabled}
                        selectedModuleId={selectedModuleIds[chapter.id]}
                        onModuleChange={(moduleId) => {
                          handleModuleChange(chapter.id, moduleId)
                        }}
                        onBook={handleBook}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </section>
          )}

          {/* Les plus populaires */}
          {popularChapters.length > 0 && (
            <section className="relative">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="text-xl font-bold text-neutral-900">Les plus populaires</h2>
              </div>
              <Carousel
                opts={{
                  align: 'start',
                }}
                className="w-full"
              >
                <div className="absolute -top-14 right-0 flex gap-2">
                  <CarouselPrevious className="static translate-y-0" />
                  <CarouselNext className="static translate-y-0" />
                </div>
                <CarouselContent noOverFlow={true}>
                  {popularChapters.map((chapter) => (
                    <CarouselItem
                      key={`popular-${chapter.id}`}
                      className="md:basis-1/2 lg:basis-1/3"
                    >
                      <BookingCard
                        chapter={chapter}
                        selectedModuleId={selectedModuleIds[chapter.id]}
                        disabled={isBookingDisabled}
                        onModuleChange={(moduleId) => {
                          handleModuleChange(chapter.id, moduleId)
                        }}
                        onBook={handleBook}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </section>
          )}

          {/* Tous les cours */}
          <section className="relative">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-neutral-900">Tous les cours</h2>
            </div>
            {chapters.length > 0 ? (
              <Carousel
                opts={{
                  align: 'start',
                }}
                className="w-full"
              >
                <div className="absolute -top-14 right-0 flex gap-2">
                  <CarouselPrevious className="static translate-y-0" />
                  <CarouselNext className="static translate-y-0" />
                </div>
                <CarouselContent noOverFlow={true}>
                  {chapters.map((chapter) => (
                    <CarouselItem key={`all-${chapter.id}`} className="md:basis-1/2 lg:basis-1/3">
                      <BookingCard
                        chapter={chapter}
                        selectedModuleId={selectedModuleIds[chapter.id]}
                        disabled={isBookingDisabled}
                        onModuleChange={(moduleId) => {
                          handleModuleChange(chapter.id, moduleId)
                        }}
                        onBook={handleBook}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            ) : (
              <p className="py-10 text-center text-neutral-500">
                Aucun cours ne correspond à votre recherche.
              </p>
            )}
          </section>
        </div>
      ) : (
        /* Mode LISTE */
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {chapters.length > 0 ? (
            chapters.map((chapter) => (
              <BookingListCard
                key={`list-${chapter.id}`}
                chapter={chapter}
                selectedModuleId={selectedModuleIds[chapter.id]}
                disabled={isBookingDisabled}
                onModuleChange={(moduleId) => {
                  handleModuleChange(chapter.id, moduleId)
                }}
                onBook={handleBook}
              />
            ))
          ) : (
            <div className="col-span-full py-10 text-center text-neutral-500">
              Aucun cours ne correspond à votre recherche.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
